import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import Navbar from '../components/Navbar.js';
import { ENDPOINTS, apiCall, parseJwt, API_BASE } from '../utils/api.js';

// Consumes an admin-login magic link (/login/magic/:token): exchanges the token for JWTs,
// stores the session + role flags (same as LoginForm), then lands on the org dashboard.
export default function MagicLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ran = useRef(false); // guard against React 18 double-invoke (link is single-use)

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const data = await apiCall(ENDPOINTS.MAGIC_LINK_CONSUME(token));
        localStorage.setItem('accessToken', data.access);
        window.dispatchEvent(new Event('sqip:loggedIn'));
        if (data.refresh) localStorage.setItem('refreshToken', data.refresh);
        if (data.id != null) localStorage.setItem('userId', data.id);
        localStorage.setItem('username', data.username || '');
        const payload = parseJwt(data.access);
        if (payload && payload.exp) localStorage.setItem('accessTokenExp', String(payload.exp * 1000));

        // Role flags + name, same shape LoginForm stores, so the app routes correctly.
        try {
          const me = await fetch(`${API_BASE}/me/`, { headers: { Authorization: `Bearer ${data.access}` } }).then((r) => r.json());
          localStorage.setItem('isStaff', me.is_staff || me.is_superuser ? 'true' : 'false');
          localStorage.setItem('isOrgAdmin', me.is_org_admin ? 'true' : 'false');
          localStorage.setItem('isSqipAdmin', me.is_sqip_admin ? 'true' : 'false');
          localStorage.setItem('orgAccess', JSON.stringify(me.org_access || []));
          localStorage.setItem('userGroups', JSON.stringify(me.groups || []));
          localStorage.setItem('firstName', me.first_name || '');
          localStorage.setItem('lastName', me.last_name || '');
        } catch (e) { /* non-fatal; nav guard still has the token */ }

        window.dispatchEvent(new Event('sqip:login'));
        navigate('/admin', { replace: true });
      } catch (e) {
        setError(e.message || 'This sign-in link is invalid or has expired.');
      }
    })();
  }, [token, navigate]);

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Navbar />
        <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 10, textAlign: 'center' }}>
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Magic links are valid for 7 days and can be used once. You can still sign in with your phone number.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>Go to login</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Signing you in…</Typography>
      </Box>
    </Box>
  );
}
